import React from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { formatMelbourneShortDate } from "../../../utils/melbourneTime";

/*
 * "This address is already in the CRM" (Michael Piccoli, CRM-2026-00081).
 *
 * 2/29 Elizabeth Street, Bulleen was keyed in by hand three times, as two
 * appraisals and a listing, and nothing warned. Genneva then texted buyers a
 * link from the unpublished appraisal and every one of them hit "Failed to
 * load property details". Contacts already get a duplicate check on add;
 * properties now do too.
 *
 * One profile per property (Christian Gravias, 1 Oct 2026: "Properties should
 * never be duplicates. The info should just merge."). When a live profile of
 * the same kind is already there, Save no longer makes a second one: "Add to
 * this profile" merges what the agent entered into it (POST
 * properties/<id>/merge-in/). A lease beside a sale is its own profile, so
 * that case still only warns. `matches` comes straight from GET
 * properties/check-duplicate/ and only ever holds LIVE profiles (not sold,
 * leased, lost, archived or in the trash).
 */

export const transactionKind = (value) =>
  String(value || "").trim().toLowerCase() === "lease" ? "lease" : "sale";

/** The profiles a new one of this kind would duplicate (sale vs lease). */
export const sameKindTwins = (matches, transactionType) =>
  (matches || []).filter(
    (m) => transactionKind(m.transaction_type) === transactionKind(transactionType)
  );

export const describeTwin = (match) => {
  const status = match.status || "Property";
  const lease = match.transaction_type === "lease" ? " for lease" : "";
  const owner = match.is_mine ? "yours" : match.agent || "no agent";
  const added = formatMelbourneShortDate(match.created_at);
  return [`${status}${lease}`, owner, added && `added ${added}`].filter(Boolean).join(" · ");
};

const TwinRow = ({ match, onOpen }) => (
  <li className="flex flex-wrap items-center justify-between gap-2">
    <span className="min-w-0">
      <span className="font-semibold">{match.address}</span>
      <span className="block text-xs text-amber-700">{describeTwin(match)}</span>
    </span>
    <button
      type="button"
      onClick={() => onOpen(match)}
      className="shrink-0 rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
    >
      Open it
    </button>
  </li>
);

/** Shown under the Address field as soon as the agent has typed an address
 *  that already has a live profile. */
export const DuplicateAddressNotice = ({ matches, onOpen, transactionType }) => {
  if (!matches || matches.length === 0) return null;
  const merges = sameKindTwins(matches, transactionType).length > 0;
  return (
    <div
      role="status"
      data-testid="duplicate-address-notice"
      className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
    >
      <p className="flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        This address is already in the CRM
      </p>
      <ul className="mt-2 space-y-2">
        {matches.map((m) => (
          <TwinRow key={m.id} match={m} onOpen={onOpen} />
        ))}
      </ul>
      <p className="mt-2 text-xs text-amber-700">
        {merges
          ? "Saving adds what you enter here to that profile, so the property keeps one profile."
          : "A lease and a sale can each have their own profile, so you can still save this one."}
      </p>
    </div>
  );
};

/** Last check when the agent presses Save with a live twin still there. */
export const DuplicateAddressDialog = ({
  isOpen, matches, transactionType, onOpen, onClose, onSaveAnyway, onMerge,
}) => {
  const list = matches || [];
  const target = sameKindTwins(list, transactionType)[0] || null;
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-[480px] rounded-2xl border-none shadow-2xl">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-amber-50 text-amber-700">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
              This address is already in the CRM
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="py-2">
          <DialogDescription className="text-sm text-gray-600 leading-relaxed">
            {target
              ? "Each property has one profile. Saving adds what you entered here (owner, note, " +
                "reminder, photos and any details it is missing) to that profile instead of " +
                "making a second one. Nothing already on it is overwritten."
              : "This address already has a profile for a different kind of campaign (a lease " +
                "beside a sale). You can save this one as its own profile."}
          </DialogDescription>
          <ul className="mt-4 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
            {list.map((m) => (
              <TwinRow key={m.id} match={m} onOpen={onOpen} />
            ))}
          </ul>
        </div>

        <DialogFooter className="gap-3 sm:gap-0 sm:flex-row flex-col">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-gray-500 bg-gray-100 border border-transparent rounded-xl hover:bg-gray-200 transition-all font-sans"
          >
            Go back
          </button>
          <button
            type="button"
            onClick={target ? () => onMerge(target) : onSaveAnyway}
            className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-white rounded-xl transition-all font-sans shadow-lg active:scale-95 bg-[#55C5D0] hover:bg-[#4AADB7]"
          >
            {target ? "Add to this profile" : "Save anyway"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/*
 * Same building, one without a unit number (Genneva Smarrelli, CRM-2026-00119;
 * Michael Piccoli asked how to stop the double up). A Meta lead made
 * "11-17 Park St, St Kilda West" with no unit, and the appraisal was later
 * keyed in as "14/11-17 Park Street". Those are not the same address, so the
 * twin check above stayed quiet. These rows come from `same_building` on
 * properties/check-duplicate/: only ever a question, never a merge, and two
 * different units (1/29 vs 2/29) are never listed.
 */
const SAME_BUILDING_HINT =
  "One of these addresses has no unit number, so it may be the same property " +
  "(often a Facebook lead that came in without the unit).";

export const SameBuildingNotice = ({ matches, onOpen }) => {
  if (!matches || matches.length === 0) return null;
  return (
    <div
      role="status"
      data-testid="same-building-notice"
      className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
    >
      <p className="flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Same building already in the CRM
      </p>
      <ul className="mt-2 space-y-2">
        {matches.map((m) => (
          <TwinRow key={m.id} match={m} onOpen={onOpen} />
        ))}
      </ul>
      <p className="mt-2 text-xs text-amber-700">{SAME_BUILDING_HINT}</p>
    </div>
  );
};

/** Asked once on Save when only a same-building record is there. */
export const SameBuildingDialog = ({ isOpen, matches, onOpen, onClose, onSaveAnyway }) => (
  <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent className="sm:max-w-[480px] rounded-2xl border-none shadow-2xl">
      <DialogHeader>
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-amber-50 text-amber-700">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
            Is this the same property?
          </DialogTitle>
        </div>
      </DialogHeader>

      <div className="py-2">
        <DialogDescription className="text-sm text-gray-600 leading-relaxed">
          {SAME_BUILDING_HINT} Open it to check, and update its address there
          instead of adding a second one. If it really is a different property,
          save this one.
        </DialogDescription>
        <ul className="mt-4 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          {(matches || []).map((m) => (
            <TwinRow key={m.id} match={m} onOpen={onOpen} />
          ))}
        </ul>
      </div>

      <DialogFooter className="gap-3 sm:gap-0 sm:flex-row flex-col">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-gray-500 bg-gray-100 border border-transparent rounded-xl hover:bg-gray-200 transition-all font-sans"
        >
          Go back
        </button>
        <button
          type="button"
          onClick={onSaveAnyway}
          className="flex-1 px-6 py-3 text-xs font-black uppercase tracking-widest text-white rounded-xl transition-all font-sans shadow-lg active:scale-95 bg-[#55C5D0] hover:bg-[#4AADB7]"
        >
          Save anyway
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
